import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, ShieldCheck, Zap, LogIn, Users } from "lucide-react";
import UpiPaymentBox from "@/components/UpiPaymentBox";

const PLAN = {
  id: "premium_monthly",
  name: "Premium",
  price_display: "₹165",
  price_usd: "$1.99",
  interval: "ತಿಂಗಳಿಗೆ",
  features: [
    "ಜಾಹೀರಾತು-ರಹಿತ ಅನುಭವ (Ad-free reading)",
    "ಪ್ರೀಮಿಯಂ ಸಬ್‌ಸ್ಕ್ರೈಬರ್ ಬ್ಯಾಡ್ಜ್",
    "ಎಲ್ಲಾ ಭಾಷೆಗಳಲ್ಲಿ ಅನಿಯಮಿತ ಅನುವಾದ (Unlimited translations)",
    "ಆದ್ಯತೆಯ ಬ್ರೇಕಿಂಗ್ ನ್ಯೂಸ್ ಎಚ್ಚರಿಕೆಗಳು",
    "ಬೆಂಬಲ ಸ್ವತಂತ್ರ ಪತ್ರಿಕೋದ್ಯಮ",
  ],
};

export default function Subscribe() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subscribe/stats");
        setStats(data);
      } catch {}
    })();
  }, []);

  const isPremium = user && user !== false && user.subscription_tier === "premium";

  const startCheckout = async () => {
    if (!user || user === false) {
      toast.error("ದಯವಿಟ್ಟು ಮೊದಲು ಲಾಗಿನ್ ಮಾಡಿ");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: PLAN.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("ಚೆಕ್‌ಔಟ್ ಪ್ರಾರಂಭಿಸಲಾಗಲಿಲ್ಲ. ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="border-b-2 border-primary pb-4 mb-10">
        <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground">Subscribe</div>
        <h1 className="font-kannada-heading text-4xl sm:text-5xl mt-1">
          ಪ್ರೀಮಿಯಂ ಸದಸ್ಯತ್ವ <span className="text-primary">.</span>
        </h1>
        <p className="font-kannada-body text-lg text-muted-foreground mt-3 max-w-2xl">
          ಜಾಹೀರಾತು ರಹಿತ ಸುದ್ದಿ ಓದಿ, ಸ್ವತಂತ್ರ ಪತ್ರಿಕೋದ್ಯಮಕ್ಕೆ ಬೆಂಬಲಿಸಿ.
        </p>
      </div>

      {isPremium && (
        <div className="mb-8 border border-primary bg-primary/5 p-5 rounded-sm flex items-center gap-3" data-testid="already-premium">
          <ShieldCheck className="w-6 h-6 text-primary" />
          <div>
            <div className="font-kannada-heading text-lg">ನೀವು ಈಗಾಗಲೇ ಪ್ರೀಮಿಯಂ ಸದಸ್ಯ!</div>
            <div className="text-sm text-muted-foreground">ಎಲ್ಲಾ ಪ್ರೀಮಿಯಂ ವೈಶಿಷ್ಟ್ಯಗಳು ಸಕ್ರಿಯವಾಗಿವೆ.</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Free tier */}
        <div className="border border-border p-8 bg-card rounded-sm">
          <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground">Free</div>
          <div className="font-kannada-heading text-3xl mt-1">ಉಚಿತ</div>
          <div className="mt-4">
            <span className="font-kannada-heading text-5xl">₹0</span>
            <span className="text-muted-foreground ml-2">ಶಾಶ್ವತ</span>
          </div>
          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex gap-2"><Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" /> ಎಲ್ಲಾ ಸುದ್ದಿಗಳು ಓದಬಹುದು</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" /> ಬುಕ್‌ಮಾರ್ಕ್ ಮತ್ತು ಕಾಮೆಂಟ್</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" /> ಜಾಹೀರಾತುಗಳೊಂದಿಗೆ</li>
          </ul>
        </div>

        {/* Premium tier */}
        <div className="border-2 border-primary p-8 bg-card rounded-sm relative" data-testid="premium-plan-card">
          <div className="absolute -top-3 right-6 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest font-black px-3 py-1 rounded-sm flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Recommended
          </div>
          <div className="font-ui text-xs uppercase tracking-[0.3em] text-primary font-bold">Premium</div>
          <div className="font-kannada-heading text-3xl mt-1">{PLAN.name}</div>

          {stats && stats.premium_total > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 border border-primary/30 bg-primary/5 px-3 py-1.5 rounded-sm" data-testid="subscribers-count">
              <Users className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-bold">
                <span className="text-primary">{stats.premium_total.toLocaleString("en-IN")}</span>{" "}
                <span className="text-muted-foreground font-semibold">ಪ್ರೀಮಿಯಂ ಸದಸ್ಯರು ಸೇರಿದ್ದಾರೆ</span>
                {stats.this_month > 0 && (
                  <span className="text-muted-foreground font-normal ml-1">
                    · {stats.this_month.toLocaleString("en-IN")} this month
                  </span>
                )}
              </span>
            </div>
          )}

          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-kannada-heading text-5xl">{PLAN.price_display}</span>
            <span className="text-muted-foreground">/ {PLAN.interval}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Billed as {PLAN.price_usd}/month · Cancel anytime
          </div>
          <ul className="mt-6 space-y-3 text-sm">
            {PLAN.features.map((f, i) => (
              <li key={i} className="flex gap-2 font-kannada-body">
                <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
          {user && user !== false ? (
            <Button
              onClick={startCheckout}
              disabled={loading || isPremium}
              className="w-full mt-8 rounded-none"
              data-testid="subscribe-btn"
            >
              <Zap className="w-4 h-4 mr-1" />
              {isPremium ? "ಸಕ್ರಿಯ" : loading ? "ಪ್ರಾರಂಭಿಸುತ್ತಿದೆ..." : "ಈಗಲೇ ಸಬ್‌ಸ್ಕ್ರೈಬ್ ಮಾಡಿ"}
            </Button>
          ) : (
            <Button asChild className="w-full mt-8 rounded-none" data-testid="subscribe-login-cta">
              <Link to="/login">
                <LogIn className="w-4 h-4 mr-1" /> ಸಬ್‌ಸ್ಕ್ರೈಬ್ ಮಾಡಲು ಲಾಗಿನ್
              </Link>
            </Button>
          )}
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-8">
        Secure payment via Stripe · Test mode · Card: <code className="bg-muted px-1.5 py-0.5 rounded-sm">4242 4242 4242 4242</code>
      </p>

      {/* Alternate: UPI / PhonePe direct payment */}
      <div className="mt-12">
        <div className="text-center mb-6">
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Or</div>
          <h2 className="font-kannada-heading text-3xl mt-1">ನೇರ ಪಾವತಿ</h2>
        </div>
        <UpiPaymentBox />
      </div>
    </div>
  );
}
